import {
    DestroyRef,
    Directive,
    ElementRef,
    InputSignal,
    OnInit,
    OutputEmitterRef,
    Renderer2,
    inject,
    input,
    output
} from "@angular/core";
import {filter, fromEvent, mergeWith, Observable, PartialObserver, Subject, switchMap, takeUntil, tap} from "rxjs";
import {Direction} from './direction';
import {onEach} from '../utils/observable-utils';

/**
 * Directive to detect swipe gestures.
 * Note that currently, swipes in a single specified direction only are supported.
 */
@Directive({
    selector: "[woa-swipable]"
})
export class SwipableDirective implements OnInit {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private element: ElementRef = inject(ElementRef);
    private renderer: Renderer2 = inject(Renderer2);

    private static readonly DEFAULT_MAX_SWIPE_DISTANCE: number = 250;

    public readonly swipeEnabled: InputSignal<boolean> = input<boolean>(true);
    public readonly swipeDirection: InputSignal<Direction> = input<Direction>(Direction.DOWN);
    public readonly bidirectional: InputSignal<boolean> = input<boolean>(false);
    public readonly swipeScale: InputSignal<number> = input<number>(1);
    public readonly swipeEase: InputSignal<boolean> = input<boolean>(false);
    public readonly swipeRangeProportionOfSize: InputSignal<number> = input<number>(0.6);
    public readonly swipeRangeDistance: InputSignal<number> = input<number>(Number.MAX_SAFE_INTEGER);
    public readonly swipeMaxDistance: InputSignal<number> = input<number>(SwipableDirective.DEFAULT_MAX_SWIPE_DISTANCE);
    public readonly swipePrimeProportionOfRange: InputSignal<number> = input<number>(0.6);
    public readonly swipeRestoreDelay: InputSignal<number> = input<number>(0);
    public readonly swipeDisengageDelay: InputSignal<number | undefined> = input<number | undefined>();
    public readonly swipeScroller: InputSignal<ElementRef | undefined> = input<ElementRef | undefined>();

    public onSwipe: OutputEmitterRef<Direction> = output<Direction>();

    private start: Touch;
    private distance: number;
    private backRange: number;
    private forthRange: number;
    private engaged: boolean;

    ngOnInit(): void {
        this.renderer.removeClass(this.element.nativeElement, "woa-swipe-engaged");
        this.renderer.removeClass(this.element.nativeElement, "woa-swipe-primed");
        this.element.nativeElement.style.setProperty("--woa-swipe-distance", 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-x-distance", 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-y-distance", 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-proportion", 0);

        if (this.swipeDirection() === Direction.NONE) {
            return;
        }

        if (this.swipeEnabled()) {
            const touchstart$: Observable<TouchEvent> = fromEvent(this.element.nativeElement, "touchstart", {passive: true});
            const touchmove$: Observable<TouchEvent> = fromEvent(this.element.nativeElement, "touchmove", {passive: false});
            const touchend$: Observable<TouchEvent> = fromEvent(this.element.nativeElement, "touchend", {passive: true});
            const swipeend$: Subject<void> = new Subject<void>();

            onEach(touchstart$.pipe(
                filter((event: TouchEvent) => this.canStartSwipe(event.target as Element)),
                tap((event: TouchEvent) => {
                    this.start = event.targetTouches[0];
                    if (this.swipeDirection() === Direction.UP || this.swipeDirection() === Direction.DOWN) {
                        const topEdge: number = this.start.clientY;
                        const bottomEdge: number = this.element.nativeElement.clientHeight - this.start.clientY;
                        const maxRange: number = Math.min(this.element.nativeElement.clientHeight * this.swipeRangeProportionOfSize(), this.swipeRangeDistance());
                        const rangeUp: number = Math.min(maxRange, this.swipeMaxDistance() ?? topEdge);
                        const rangeDown: number = Math.min(maxRange, this.swipeMaxDistance() ?? bottomEdge);
                        this.forthRange = this.swipeDirection() === Direction.UP ? rangeUp : rangeDown;
                        this.backRange = !this.bidirectional() ? 0 : Direction.UP ? rangeDown : rangeUp;
                    } else {
                        const leftEdge: number = this.start.clientX;
                        const rightEdge: number = this.element.nativeElement.clientWidth - this.start.clientX;
                        const maxRange: number = Math.min(this.element.nativeElement.clientWidth * this.swipeRangeProportionOfSize(), this.swipeRangeDistance());
                        const rangeLeft: number = Math.min(maxRange, this.swipeMaxDistance() ?? leftEdge);
                        const rangeRight: number = Math.min(maxRange, this.swipeMaxDistance() ?? rightEdge);
                        this.forthRange = this.swipeDirection() === Direction.LEFT ? rangeLeft : rangeRight;
                        this.backRange = !this.bidirectional() ? 0 : Direction.LEFT ? rangeRight : rangeLeft;
                    }
                }),
                switchMap(() => touchmove$.pipe(
                    tap((event: TouchEvent) => {
                        let distance: number = this.swipeScale() * this.calculateDistance(event);

                        // Determine the swipe direction based on the first observed vertical (for UP/DOWN) or horizontal (for LEFT/RIGHT) movement.
                        // Unless bidirectional, terminate on an initial swipe in the opposite direction to that required.
                        if (!this.engaged) {
                            if (distance == 0) {
                                return;
                            }
                            if (distance < 0 && !this.bidirectional()) {
                                this.setDistance(0);
                                this.setEngaged(false);
                                swipeend$.next();
                                return;
                            }
                            this.setEngaged(true);
                        }

                        distance = Math.min(Math.max(distance, -this.backRange), this.forthRange);
                        if (this.swipeEase()) {
                            distance = distance >= 0
                                ? SwipableDirective.easeInQuadOutQuart(distance, 0, this.forthRange, this.forthRange)
                                : -SwipableDirective.easeInQuadOutQuart(-distance, 0, this.backRange, this.backRange);
                        }
                        this.setDistance(distance);

                        // Prevent any page content from scrolling at the same time.
                        event.preventDefault();
                    }),
                    takeUntil(touchend$.pipe(
                        tap(() => {
                            if (this.swipePrimed) {
                                this.onSwipe.emit(this.distance >= 0 ? this.swipeDirection()
                                    : this.swipeDirection() === Direction.UP ? Direction.DOWN
                                        : this.swipeDirection() === Direction.DOWN ? Direction.UP
                                            : this.swipeDirection() === Direction.LEFT ? Direction.RIGHT
                                                : Direction.LEFT);
                                setTimeout(() => this.setDistance(0), this.swipeRestoreDelay());
                            } else {
                                this.setDistance(0);
                            }
                            this.setEngaged(false);
                        }),
                        mergeWith(swipeend$)
                    ))
                ))
            ), {} as PartialObserver<TouchEvent>, this.destroyRef);
        }
    }

    private get swipePrimed(): boolean {
        return this.distance >= this.forthRange * this.swipePrimeProportionOfRange() || (this.bidirectional() && -this.distance >= this.backRange * this.swipePrimeProportionOfRange());
    }

    private get scrollerAtLimit(): boolean {
        switch (this.swipeDirection()) {
            case Direction.DOWN:
                return this.swipeScroller()!.nativeElement.scrollTop <= 0;
            case Direction.UP:
                return this.swipeScroller()!.nativeElement.scrollTop + this.swipeScroller()!.nativeElement.clientHeight >= this.swipeScroller()!.nativeElement.scrollHeight;
            case Direction.RIGHT:
                return this.swipeScroller()!.nativeElement.scrollLeft <= 0;
            case Direction.LEFT:
                return this.swipeScroller()!.nativeElement.scrollLeft + this.swipeScroller()!.nativeElement.clientWidth >= this.swipeScroller()!.nativeElement.scrollWidth;
            default:
                return false;
        }
    }

    private calculateDistance(event: TouchEvent) {
        switch (this.swipeDirection()) {
            case Direction.DOWN:
                return event.targetTouches[0].screenY - this.start.screenY;
            case Direction.UP:
                return this.start.screenY - event.targetTouches[0].screenY;
            case Direction.RIGHT:
                return event.targetTouches[0].screenX - this.start.screenX;
            case Direction.LEFT:
                return this.start.screenX - event.targetTouches[0].screenX;
            default:
                return 0;
        }
    }

    private setEngaged(engaged: boolean): void {
        this.engaged = engaged;
        if (engaged) {
            this.renderer.addClass(this.element.nativeElement, "woa-swipe-engaged");
        } else if (this.swipePrimed) {
            this.renderer.removeClass(this.element.nativeElement, "woa-swipe-engaged");
        } else {
            this.renderer.addClass(this.element.nativeElement, "woa-swipe-disengaging");
            setTimeout(() => {
                this.renderer.removeClass(this.element.nativeElement, "woa-swipe-disengaging");
                this.renderer.removeClass(this.element.nativeElement, "woa-swipe-engaged");
            }, this.swipeDisengageDelay());
        }
    }

    private setDistance(distance: number): void {
        this.distance = distance;
        this.element.nativeElement.style.setProperty("--woa-swipe-distance", distance);
        this.element.nativeElement.style.setProperty("--woa-swipe-x-distance", this.swipeDirection() === Direction.RIGHT ? distance : this.swipeDirection() === Direction.LEFT ? -distance : 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-y-distance", this.swipeDirection() === Direction.DOWN ? distance : this.swipeDirection() === Direction.UP ? -distance : 0);
        this.element.nativeElement.style.setProperty("--woa-swipe-proportion", distance >= 0 ? distance / (this.forthRange * this.swipePrimeProportionOfRange()) : -distance / (this.backRange * this.swipePrimeProportionOfRange()));
        if (this.swipePrimed) {
            this.renderer.addClass(this.element.nativeElement, "woa-swipe-primed");
        } else {
            this.renderer.removeClass(this.element.nativeElement, "woa-swipe-primed");
        }
    }

    private static easeInQuadOutQuart(t: number, b: number, c: number, d: number): number {
        t /= d / 2;
        return t < 1 ? c / 2 * t * t + b : -c / 2 * ((t -= 2) * t * t * t - 2) + b;
    }

    private canStartSwipe(element: Element): boolean {
        return this.swipeEnabled() && (this.bidirectional() || !this.swipeScroller() || this.scrollerAtLimit || !this.isScrollerChild(element));
    }

    private isScrollerChild(element: Element) {
        return element === this.swipeScroller()!.nativeElement || element && this.isScrollerChild(element.parentElement);
    }
}
